import { ReactNode } from 'react';

import {
  TableColumnV2,
  TableSortMode,
  TableSortModeValue,
  TagColor,
  TagProps,
} from '@equip.health/ui';

import { InquiryEvents } from '~/analytics';
import {
  InquiryStatusValue,
  InquiryViewType,
} from '~/lib/constants/inquiry.constants';
import {
  CareTeamProviderType,
  CareTeamProviderTypeNames,
} from '~/lib/constants/patients.constants';

export const getOrderByDirection = (sortMode: TableSortModeValue) => {
  if (sortMode === TableSortMode.asc) {
    return 'secondary';
  }

  if (sortMode === TableSortMode.desc) {
    return 'primary';
  }

  return undefined;
};

export const InquiryStatusTagDropdownOptions: TagProps[] = [
  {
    color: TagColor.marigold,
    id: `inquiry-status__new`,
    value: InquiryStatusValue.NEW,
  },
  {
    color: TagColor.periwinkle,
    id: `inquiry-status__fielded`,
    value: InquiryStatusValue.FIELDED,
  },
  {
    color: TagColor.purple,
    id: `inquiry-status__qualified`,
    value: InquiryStatusValue.QUALIFIED,
  },
  {
    color: TagColor.orange,
    id: `inquiry-status__assessing`,
    value: InquiryStatusValue.ASSESSING,
  },
  {
    color: TagColor.pink,
    id: `inquiry-status__onboarding`,
    value: InquiryStatusValue.ONBOARDING,
  },
  {
    color: TagColor.green,
    id: `inquiry-status__enrolled`,
    value: InquiryStatusValue.ENROLLED,
  },
  {
    color: TagColor.red,
    id: `inquiry-status__closed`,
    value: InquiryStatusValue.CLOSED,
  },
];

export const InquiryChannelTagColorMap: Record<InquiryChannel, TagColor> = {
  Direct: TagColor.marigold,
  Referral: TagColor.periwinkle,
};

export interface InquiryTableColumn {
  admissionsOperationsRep: ReactNode;
  admissionsSpecialist: ReactNode;
  admissionsSupervisor: ReactNode;
  patientName: ReactNode;
  status: ReactNode;
  patientServiceRep: ReactNode;
  primaryContact: ReactNode;
  lastContactDate: ReactNode;
  nextContactDate: ReactNode;
  timezone: ReactNode;
  preferredLanguage: ReactNode;
  channel: ReactNode;
  channelType: ReactNode;
  modeOfInquiry: ReactNode;
  createdDate: ReactNode;
  qcApptDate: ReactNode;
  patientState?: ReactNode;
  referralCoordinator?: ReactNode;
  insuranceReviewStatus?: ReactNode;
  medicalClearanceStatus?: ReactNode;
  targetAdmitDate?: ReactNode;
  targetTreatmentProgram?: ReactNode;
  intakeSurveyStatus?: ReactNode;
  preliminaryDiagnosis?: ReactNode;
  inquiryNextSteps?: ReactNode;
  insuranceCompanyName?: ReactNode;
  medicalAppointmentDate?: ReactNode;
}

enum TableColumnWidth {
  regular = '240px',
  small = '180px',
}

export const InquiryTableColumnKeys = {
  admissionsOperationsRep: 'admissionsOperationsRep',
  admissionsSpecialist: 'admissionsSpecialist',
  admissionsSupervisor: 'admissionsSupervisor',
  channel: 'channel',
  channelType: 'channelType',
  createdDate: 'createdDate',
  inquiryNextSteps: 'inquiryNextSteps',
  insuranceCompanyName: 'insuranceCompanyName',
  insuranceReviewStatus: 'insuranceReviewStatus',
  intakeSurveyStatus: 'intakeSurveyStatus',
  lastContactDate: 'lastContactDate',
  medicalAppointmentDate: 'medicalAppointmentDate',
  medicalClearanceStatus: 'medicalClearanceStatus',
  modeOfInquiry: 'modeOfInquiry',
  nextContactDate: 'nextContactDate',
  patientName: 'patientName',
  patientServiceRep: 'patientServiceRep',
  patientState: 'patientState',
  preferredLanguage: 'preferredLanguage',
  preliminaryDiagnosis: 'preliminaryDiagnosis',
  primaryContact: 'primaryContact',
  qcApptDate: 'qcApptDate',
  referralCoordinator: 'referralCoordinator',
  status: 'status',
  targetAdmitDate: 'targetAdmitDate',
  targetTreatmentProgram: 'targetTreatmentProgram',
  timezone: 'timezone',
} as const;

export enum InquiryTableColumnSortingKeys {
  admissionsOperationsRep = 'provider-ao-info',
  admissionsSpecialist = 'provider-as-info',
  admissionsSupervisor = 'provider-admissions-supervisor-info',
  channel = 'inquiry-channel',
  channelType = 'inquiry-channel-type',
  createdDate = 'inquiry-date',
  insuranceReviewStatus = 'insurance-company-review-status',
  insuranceCompanyName = 'insurance-company-name',
  intakeSurveyStatus = 'intake-survey-status',
  lastContactDate = 'last-contact-date',
  medicalAppointmentDate = 'medical-appointment-date',
  medicalClearanceStatus = 'medical-clearance-status',
  modeOfInquiry = 'information-source',
  nextContactDate = 'next-contact-date',
  inquiryNextSteps = 'inquiry-next-steps',
  patientName = 'patient-info',
  patientServiceRep = 'provider-psr-info',
  patientState = 'patient-address-state',
  preferredLanguage = 'preferred-language',
  preliminaryDiagnosis = 'preliminary-diagnosis',
  primaryContact = 'primary-contact-info',
  qcApptDate = 'qc-appointment-date',
  referralCoordinator = 'provider-rc-info',
  targetTreatmentProgram = 'target-treatment-program',
  status = 'inquiry-status',
  targetAdmitDate = 'target-admit-date',
  timezone = 'timezone',
}

export const InquiryTableColumnKeyMap: Record<
  string,
  keyof InquiryTableColumn
> = {
  [InquiryTableColumnSortingKeys.admissionsOperationsRep]:
    InquiryTableColumnKeys.admissionsOperationsRep,
  [InquiryTableColumnSortingKeys.admissionsSpecialist]:
    InquiryTableColumnKeys.admissionsSpecialist,
  [InquiryTableColumnSortingKeys.admissionsSupervisor]:
    InquiryTableColumnKeys.admissionsSupervisor,
  [InquiryTableColumnSortingKeys.channel]: InquiryTableColumnKeys.channel,
  [InquiryTableColumnSortingKeys.channelType]:
    InquiryTableColumnKeys.channelType,
  [InquiryTableColumnSortingKeys.createdDate]:
    InquiryTableColumnKeys.createdDate,
  [InquiryTableColumnSortingKeys.inquiryNextSteps]:
    InquiryTableColumnKeys.inquiryNextSteps,
  [InquiryTableColumnSortingKeys.insuranceCompanyName]:
    InquiryTableColumnKeys.insuranceCompanyName,
  [InquiryTableColumnSortingKeys.insuranceReviewStatus]:
    InquiryTableColumnKeys.insuranceReviewStatus,
  [InquiryTableColumnSortingKeys.intakeSurveyStatus]:
    InquiryTableColumnKeys.intakeSurveyStatus,
  [InquiryTableColumnSortingKeys.lastContactDate]:
    InquiryTableColumnKeys.lastContactDate,
  [InquiryTableColumnSortingKeys.medicalAppointmentDate]:
    InquiryTableColumnKeys.medicalAppointmentDate,
  [InquiryTableColumnSortingKeys.medicalClearanceStatus]:
    InquiryTableColumnKeys.medicalClearanceStatus,
  [InquiryTableColumnSortingKeys.modeOfInquiry]:
    InquiryTableColumnKeys.modeOfInquiry,
  [InquiryTableColumnSortingKeys.nextContactDate]:
    InquiryTableColumnKeys.nextContactDate,
  [InquiryTableColumnSortingKeys.patientName]:
    InquiryTableColumnKeys.patientName,
  [InquiryTableColumnSortingKeys.patientServiceRep]:
    InquiryTableColumnKeys.patientServiceRep,
  [InquiryTableColumnSortingKeys.patientState]:
    InquiryTableColumnKeys.patientState,
  [InquiryTableColumnSortingKeys.preferredLanguage]:
    InquiryTableColumnKeys.preferredLanguage,
  [InquiryTableColumnSortingKeys.preliminaryDiagnosis]:
    InquiryTableColumnKeys.preliminaryDiagnosis,
  [InquiryTableColumnSortingKeys.primaryContact]:
    InquiryTableColumnKeys.primaryContact,
  [InquiryTableColumnSortingKeys.qcApptDate]: InquiryTableColumnKeys.qcApptDate,
  [InquiryTableColumnSortingKeys.referralCoordinator]:
    InquiryTableColumnKeys.referralCoordinator,
  [InquiryTableColumnSortingKeys.status]: InquiryTableColumnKeys.status,
  [InquiryTableColumnSortingKeys.targetAdmitDate]:
    InquiryTableColumnKeys.targetAdmitDate,
  [InquiryTableColumnSortingKeys.timezone]: InquiryTableColumnKeys.timezone,
  [InquiryTableColumnSortingKeys.targetTreatmentProgram]:
    InquiryTableColumnKeys.targetTreatmentProgram,
};

export interface InquiryViewSchema extends TableColumnV2<InquiryTableColumn> {
  columnOrder: number;
  filterKey?: string;
  filterOrder?: number;
  name: string;
  isEditable: boolean;
  isFilterable: boolean;
  isSortable: boolean;
  sortingKey?: InquiryTableColumnSortingKeys;
  sortMode?: TableSortModeValue;
  width?: TableColumnWidth;
}
export enum QCAppointmentOptions {
  SCHEDULED = 'Scheduled',
  NOT_SCHEDULED = 'Not scheduled',
}

export type GetInquiryViewsSchemaResponse = {
  data: {
    code: InquiryViewType;
    columns: InquiryViewSchema[];
  };
  isLoading: boolean;
  status?: string;
};

const basePSRViewColumns: TableColumnV2<InquiryTableColumn>[] = [
  {
    header: 'Patient name',
    key: 'patientName',
    sortingKey: 'patient-first-name',
    width: TableColumnWidth.regular,
  },
  {
    header: 'Status',
    key: 'status',
    sortingKey: 'inquiry-status',
    width: TableColumnWidth.small,
  },
  {
    header: 'Patient Service Rep',
    key: 'patientServiceRep',
    sortingKey: 'psr-first-name',
    width: TableColumnWidth.regular,
  },
  {
    header: 'Primary contact',
    key: 'primaryContact',
    width: TableColumnWidth.regular,
  },
  {
    header: 'Last contact',
    key: 'lastContactDate',
    sortingKey: 'last-contact-date',
    width: TableColumnWidth.small,
  },
  {
    header: 'Next contact',
    key: 'nextContactDate',
    sortingKey: 'next-contact-date',
    width: TableColumnWidth.small,
  },
  {
    header: 'Timezone',
    key: 'timezone',
    sortingKey: 'timezone',
    width: TableColumnWidth.small,
  },
  {
    header: 'Preferred language',
    key: 'preferredLanguage',
    width: TableColumnWidth.small,
  },
  {
    header: 'QC Appt.',
    key: 'qcApptDate',
    sortingKey: 'is-qc-scheduled',
    width: TableColumnWidth.small,
  },
  { header: 'Channel', key: 'channel', width: TableColumnWidth.small },
  { header: 'Channel type', key: 'channelType', width: TableColumnWidth.small },
  {
    header: 'Mode of inquiry',
    key: 'modeOfInquiry',
    width: TableColumnWidth.small,
  },
  {
    header: 'Created',
    key: 'createdDate',
    sortingKey: 'inquiry-date',
    width: TableColumnWidth.small,
  },
];

const rcColumns: TableColumnV2<InquiryTableColumn>[] = [
  {
    header: 'Patient state',
    key: 'patientState',
    width: TableColumnWidth.small,
  },
  {
    header: 'Referral Coordinator',
    key: 'referralCoordinator',
    sortingKey: 'rc-first-name',
    width: TableColumnWidth.regular,
  },
];

export const getInquiryTableColumns =
  (): TableColumnV2<InquiryTableColumn>[] => [
    ...basePSRViewColumns,
    ...rcColumns,
  ];

export const providerTypeRoleMapping = {
  [CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE]: 'assignedPSRs',
  [CareTeamProviderType.PROVIDER_PARTNERSHIPS]: 'assignedRCs',
  [CareTeamProviderType.ADMISSIONS_SPECIALIST]: 'assignedASs',
  [CareTeamProviderType.ADMISSIONS_OPERATIONS_COORDINATOR]: 'assignedAOs',
  [CareTeamProviderType.ADMISSIONS_SUPERVISOR]: 'assignedAdmissionsSupervisors',
};

export const providerTypeNameMapping = {
  [CareTeamProviderTypeNames[
    CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE
  ]]: 'assignedPSRs',
  [CareTeamProviderTypeNames[CareTeamProviderType.PROVIDER_PARTNERSHIPS]]:
    'assignedRCs',
  [CareTeamProviderTypeNames[CareTeamProviderType.ADMISSIONS_SPECIALIST]]:
    'assignedASs',
  [CareTeamProviderTypeNames[
    CareTeamProviderType.ADMISSIONS_OPERATIONS_COORDINATOR
  ]]: 'assignedAOs',
  [CareTeamProviderTypeNames[CareTeamProviderType.ADMISSIONS_SUPERVISOR]]:
    'assignedAdmissionsSupervisors',
};

export const getAssigneesForRole = (
  inquiry: InquiryListItemResponse,
  role: CareTeamProviderType,
): InquiryListItemAssignment[] => {
  const inquiryField = providerTypeRoleMapping[role];
  return inquiryField ? inquiry[inquiryField] ?? [] : [];
};

const compare = (
  actualProviderType: string,
  expectedProviderType: CareTeamProviderType,
): boolean => {
  return actualProviderType?.toLowerCase() === expectedProviderType;
};

export const isPSR = (providerType: string): boolean =>
  compare(providerType, CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE);

export const isRC = (providerType: string): boolean =>
  compare(providerType, CareTeamProviderType.PROVIDER_PARTNERSHIPS);

export const isAS = (providerType: string): boolean =>
  compare(providerType, CareTeamProviderType.ADMISSIONS_SPECIALIST);

export const isSupervisor = (providerType: string): boolean =>
  compare(providerType, CareTeamProviderType.ADMISSIONS_SUPERVISOR);

export const getQuickAssignTrackingEvent = (
  providerType: CareTeamProviderType,
): InquiryEvents => {
  if (isRC(providerType)) return InquiryEvents.rcAssigned;
  if (isAS(providerType)) return InquiryEvents.asAssigned;
  if (isSupervisor(providerType)) return InquiryEvents.supervisorAssigned;
  return InquiryEvents.psrAssigned;
};

export const getQuickUnassignTrackingEvent = (
  providerType: CareTeamProviderType,
): InquiryEvents => {
  if (isRC(providerType)) return InquiryEvents.rcUnassigned;
  if (isAS(providerType)) return InquiryEvents.asUnassigned;
  if (isSupervisor(providerType)) return InquiryEvents.supervisorUnassigned;
  return InquiryEvents.psrUnassigned;
};
